import {ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {
  DashboardGroup,
  GROUP_ITEM_FOLDER_CLASS,
  STATUS_GROUP_PAGE_DATA_KEYS,
  StatusGroupPageData
} from '@models/dashboard/dashboard-group';
import {Router} from '@angular/router';
import {StorageService} from '@services/storage/storage.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {MenuItem} from '@models/sidebar/menu-item';
import {DashboardService} from '@services/dashboard/dashboard.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {MenuItemType} from '@models/sidebar/menu-item-type';

@Component({
    selector: 'scitis-dashboard-group-item',
    templateUrl: './group-item.component.html',
    styleUrls: ['./group-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GroupItemComponent implements OnChanges, OnDestroy {

  @Input()
  group: DashboardGroup | any;
  hovering: boolean = false;
  machineStatusEventDetails: { machineCount: number, className: string, key: string }[] = [];

  loadingMenuItem = false;
  haloFolderClass: string = '';
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private router: Router,
              private storage: StorageService,
              private dashboardService: DashboardService,
              private loadingIndicator: LoadingIndicatorService,
              private menuNotificationService: MenuNotificationService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.group?.currentValue?.statusGroupPageData) {
      this.statusPageDataSetup();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onGroupClick() {
    this.loadingMenuItem = true;
    this.dashboardService.getItemMenuItemAndParentMenuItem(String(this.group.groupId), false)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (menuItem: MenuItem) => {
        this.loadingMenuItem = false;
        this.focusOnLeftMenu(menuItem);
        this.storage.writeValueToIndexedDb('activeGroupName', this.group.name).then(() => {
          this.redirectTo(this.group.groupId, this.group.name);
        });
      }, error: error => {
        console.log(error);
      }
    });
  }

  onFolderClick(event) {
    console.log('test click on status group folder');
    event.stopPropagation();
    this.loadingMenuItem = true;
    this.loadingIndicator.show();
    if (this.group?.statusGroupPageData) {
      this.dashboardService.getGroupStatusPageMenuItem(String(this.group.groupId))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (menuItems: MenuItem) => {
          if (menuItems) {
            if (MenuItemType.MACHINE_GROUP_STATUS_PAGE === menuItems.type) {
              this.menuNotificationService.broadcastRedirectToGroupStatusPage(menuItems);
              this.focusOnLeftMenu(menuItems);
            }else{
              this.focusOnLeftMenu(menuItems);
              this.storage.writeValueToIndexedDb('activeGroupName', this.group.name).then(() => {
                this.redirectTo(this.group.groupId, this.group.name);
              });
            }
            this.loadingIndicator.hide();
            this.loadingMenuItem = false;
          }
        }, error: error => {
          console.log(error);
        }
      });
    } else {
      this.onGroupClick();
    }
  }

  redirectTo(groupId: number, groupName: string) {
    this.router.navigateByUrl('/', {skipLocationChange: true}).then(() =>
        this.router.navigate(['dashboard', {id: groupId, machineGroupName: groupName}]));
  }

  private statusPageDataSetup(): void {
    const {statusGroupPageData} = this.group;
    if (Object.values(statusGroupPageData)?.length) {
      const classNameObject = {
        [STATUS_GROUP_PAGE_DATA_KEYS.FAILURE]: 'fa-triangle-exclamation text-danger',
        [STATUS_GROUP_PAGE_DATA_KEYS.WARNING]: 'fa-circle-exclamation text-warning',
        [STATUS_GROUP_PAGE_DATA_KEYS.INFO]: 'fa-circle-info text-info',
        [STATUS_GROUP_PAGE_DATA_KEYS.DEBUG]: 'fa-tools text-debug',
        [STATUS_GROUP_PAGE_DATA_KEYS.INHIBIT]: 'fa-ban text-inhibit',
      };

      Object.keys(statusGroupPageData).forEach(key => {
        const detail: {
          machineCount: number;
          className: string;
          key: string;
        } = {machineCount: statusGroupPageData[key], className: classNameObject[key], key: key};
        if (!this.machineStatusEventDetails.find(eventDetail => eventDetail.key === detail.key)
            && key !== STATUS_GROUP_PAGE_DATA_KEYS.OFFLINE) {
          this.machineStatusEventDetails.push(detail);
        }
      });
      this.haloFolderClass = this.determineStatusClass(statusGroupPageData);
    }
  }

  private determineStatusClass(data: StatusGroupPageData): string {
    if (data[STATUS_GROUP_PAGE_DATA_KEYS.FAILURE]) {
      return GROUP_ITEM_FOLDER_CLASS.FAILURE;
    }
    if (data[STATUS_GROUP_PAGE_DATA_KEYS.WARNING]) {
      return GROUP_ITEM_FOLDER_CLASS.WARNING;
    }
    if (data[STATUS_GROUP_PAGE_DATA_KEYS.INFO]) {
      return GROUP_ITEM_FOLDER_CLASS.INFO;
    }
    if (data[STATUS_GROUP_PAGE_DATA_KEYS.DEBUG]) {
      return GROUP_ITEM_FOLDER_CLASS.DEBUG;
    }
    if (data[STATUS_GROUP_PAGE_DATA_KEYS.INHIBIT]) {
      return GROUP_ITEM_FOLDER_CLASS.INHIBIT;
    }
    return GROUP_ITEM_FOLDER_CLASS.OFFLINE;
  }

  private focusOnLeftMenu(menuItem: MenuItem): void {
    this.menuNotificationService.broadcastClickOnDashboard(menuItem);
  }
}
